import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, KeyRound, Loader2, Users, ShieldCheck, Pencil, Lock, LockOpen } from "lucide-react";

const BLANK = { name: "", email: "", password: "", role: "sales_executive", title: "", cities: [], city_scope_label: "" };

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [districts, setDistricts] = useState([]);
  const [selState, setSelState] = useState("");
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/locations/states").then((r) => setStates(r.data.states)); }, []);
  useEffect(() => {
    if (selState) api.get("/locations/districts", { params: { state: selState } }).then((r) => setDistricts(r.data.districts));
    else setDistricts([]);
  }, [selState]);

  const toggleCity = (c) => setForm((p) => ({ ...p, cities: p.cities.includes(c) ? p.cities.filter((x) => x !== c) : [...p.cities, c] }));

  const openEdit = (u) => { setEditForm({ id: u.id, name: u.name, title: u.title || "", role: u.role, cities: u.cities || [] }); setSelState(""); };
  const editToggleCity = (c) => setEditForm((p) => ({ ...p, cities: p.cities.includes(c) ? p.cities.filter((x) => x !== c) : [...p.cities, c] }));
  const saveEdit = async () => {
    if (!editForm.name) { toast.error("Name is required"); return; }
    try {
      await api.patch(`/users/${editForm.id}`, {
        name: editForm.name, title: editForm.title, role: editForm.role,
        cities: editForm.role === "management" ? [] : editForm.cities,
        city_scope_label: editForm.role === "management" ? "All Properties" : editForm.cities.join(", "),
      });
      toast.success("User access updated");
      setEditForm(null);
      setSelState("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const createUser = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password are required"); return; }
    setSaving(true);
    try {
      await api.post("/users", { ...form, city_scope_label: form.role === "management" ? "All Properties" : form.cities.join(", ") });
      toast.success("User created");
      setAddOpen(false);
      setForm(BLANK);
      setSelState("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const changePw = async () => {
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try { await api.patch(`/users/${pwUser.id}`, { password: newPw }); toast.success("Password updated"); setPwUser(null); setNewPw(""); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const unlock = async (u) => {
    try { await api.post(`/users/${u.id}/unlock`); toast.success(`${u.name} unlocked`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const del = async () => {
    try { await api.delete(`/users/${toDelete.id}`); toast.success("User deleted"); setToDelete(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage sales executives and management accounts, scopes and passwords.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-user-button"><Plus className="h-4 w-4 mr-2" /> Add User</Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="user-management-table">
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
                <TableHead>City Scope</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="h-32 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-primary" /></TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}{u.id === me.id && <span className="text-xs text-primary ml-1">(you)</span>}<div className="text-xs text-muted-foreground font-normal">{u.title}</div></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border font-medium ${u.role === "management" ? "bg-primary/10 text-primary border-primary/30" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                      {u.role === "management" ? <ShieldCheck className="h-3 w-3" /> : <Users className="h-3 w-3" />}
                      {u.role === "management" ? "Management" : "Sales Executive"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.city_scope_label || (u.cities || []).join(", ") || "All"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} disabled={u.id === me.id || u.protected} data-testid={`user-active-${u.id}`} />
                      {u.locked && (
                        <>
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700 font-medium" data-testid={`user-locked-badge-${u.id}`}><Lock className="h-3 w-3" /> Locked</span>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => unlock(u)} data-testid={`unlock-user-button-${u.id}`}><LockOpen className="h-3 w-3 mr-1" /> Unlock</Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-button-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                      {(!u.protected || u.id === me.id) && (
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setPwUser(u)} data-testid={`change-password-button-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
                      )}
                      {u.id !== me.id && !u.protected && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setToDelete(u)} data-testid={`delete-user-button-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add user */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Add User</DialogTitle><DialogDescription>Create a sales executive or management account.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-user-title" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Temporary Password *</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, cities: [] })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sales_executive">Sales Executive</SelectItem><SelectItem value="management">Management</SelectItem></SelectContent>
              </Select>
            </div>
            {form.role === "sales_executive" && (
              <div className="space-y-2 rounded-lg border border-border p-3">
                <Label className="text-xs">Assign City / District Scope</Label>
                <Select value={selState} onValueChange={setSelState}>
                  <SelectTrigger data-testid="new-user-state"><SelectValue placeholder="Pick a state to list districts" /></SelectTrigger>
                  <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                {districts.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                    {districts.map((d) => (
                      <button key={d} type="button" onClick={() => toggleCity(d)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.cities.includes(d) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary"}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                )}
                {form.cities.length > 0 && <p className="text-xs text-muted-foreground">Assigned: {form.cities.join(", ")}</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={createUser} disabled={saving} data-testid="save-user-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create User"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user access */}
      <Dialog open={!!editForm} onOpenChange={(o) => { if (!o) { setEditForm(null); setSelState(""); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit User Access</DialogTitle><DialogDescription>Update role, title and assigned city scope.</DialogDescription></DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Full Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-user-title" /></div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v, cities: v === "management" ? [] : editForm.cities })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="sales_executive">Sales Executive</SelectItem><SelectItem value="management">Management</SelectItem></SelectContent>
                </Select>
              </div>
              {editForm.role === "sales_executive" && (
                <div className="space-y-2 rounded-lg border border-border p-3">
                  <Label className="text-xs">Assigned City / District Scope</Label>
                  <Select value={selState} onValueChange={setSelState}>
                    <SelectTrigger data-testid="edit-user-state"><SelectValue placeholder="Pick a state to list districts" /></SelectTrigger>
                    <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  {districts.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                      {districts.map((d) => (
                        <button key={d} type="button" onClick={() => editToggleCity(d)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${editForm.cities.includes(d) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary"}`}>
                          {d}
                        </button>
                      ))}
                    </div>
                  )}
                  {editForm.cities.length > 0 && <p className="text-xs text-muted-foreground">Assigned: {editForm.cities.join(", ")}</p>}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditForm(null); setSelState(""); }}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="save-edit-user-button">Save Access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change password */}
      <Dialog open={!!pwUser} onOpenChange={(o) => { if (!o) { setPwUser(null); setNewPw(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-xl">Change Password</DialogTitle><DialogDescription>Set a new password for {pwUser?.name}.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label className="text-xs">New Password</Label><Input value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setPwUser(null); setNewPw(""); }}>Cancel</Button>
            <Button onClick={changePw} data-testid="confirm-password-button">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete user?</AlertDialogTitle><AlertDialogDescription>Remove <strong>{toDelete?.name}</strong> permanently.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-user-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
